/**
 * TicketListPage — lists support tickets with filters, pagination, and a
 * create-ticket modal.
 *
 * Route: /help/tickets
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, RefreshCw, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  supportApi,
  type TicketStatus,
  type TicketPriority,
  type Ticket,
} from '../api/support';
import { TicketForm } from '../components/TicketForm';

const STATUSES: { value: TicketStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_on_customer', label: 'Waiting on Customer' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const PRIORITY_COLOR: Record<TicketPriority, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const STATUS_COLOR: Record<TicketStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  waiting_on_customer: 'bg-amber-100 text-amber-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-700',
};

function Badge({ text, cls }: { text: string; cls: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {text.replace(/_/g, ' ')}
    </span>
  );
}

export function TicketListPage() {
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('open');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allTickets, setAllTickets] = useState<Ticket[]>([]);
  const [showCreate, setShowCreate] = useState(false);

  const queryKey = ['tickets', statusFilter, cursor];

  const { isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      supportApi.listTickets({
        ...(statusFilter ? { status: statusFilter } : {}),
        cursor,
      }),
    onSuccess: (res) => {
      if (!cursor) {
        // first page — replace
        setAllTickets(res.tickets);
      } else {
        setAllTickets((prev) => {
          const ids = new Set(prev.map((t) => t.id));
          return [...prev, ...res.tickets.filter((t) => !ids.has(t.id))];
        });
      }
      setNextCursor(res.next_cursor);
    },
  });

  const [nextCursor, setNextCursor] = useState<string | null>(null);

  function handleStatusChange(val: TicketStatus | '') {
    setStatusFilter(val);
    setCursor(undefined);
    setAllTickets([]);
  }

  function handleLoadMore() {
    if (nextCursor) {
      setCursor(nextCursor);
    }
  }

  function handleCreated() {
    setShowCreate(false);
    setCursor(undefined);
    setAllTickets([]);
    void refetch();
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
          <p className="mt-1 text-sm text-gray-500">Track and manage customer support requests</p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4" /> New Ticket
        </button>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Create Support Ticket</h2>
            <TicketForm onCreated={handleCreated} onCancel={() => setShowCreate(false)} />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => handleStatusChange(s.value as TicketStatus | '')}
            className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
              statusFilter === s.value
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {s.label}
          </button>
        ))}

        <button
          type="button"
          onClick={() => { setCursor(undefined); setAllTickets([]); void refetch(); }}
          className="ml-auto text-gray-400 hover:text-gray-600"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* List */}
      {isLoading && allTickets.length === 0 && (
        <div className="py-12 text-center text-sm text-gray-400">Loading tickets…</div>
      )}
      {isError && (
        <div className="rounded bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Failed to load tickets. Please try again.
        </div>
      )}
      {!isLoading && !isError && allTickets.length === 0 && (
        <div className="py-12 text-center text-sm text-gray-400">No tickets found.</div>
      )}

      <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white overflow-hidden">
        {allTickets.map((ticket) => (
          <li key={ticket.id}>
            <Link
              to={`/help/tickets/${ticket.id}`}
              className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-900 truncate">{ticket.subject}</span>
                  <Badge text={ticket.status} cls={STATUS_COLOR[ticket.status]} />
                  <Badge text={ticket.priority} cls={PRIORITY_COLOR[ticket.priority]} />
                </div>
                <p className="mt-0.5 text-xs text-gray-500 truncate">
                  {ticket.category} &middot;{' '}
                  {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                  {ticket.sla_due_at && (
                    <span className="ml-2 text-amber-600">
                      SLA: {formatDistanceToNow(new Date(ticket.sla_due_at), { addSuffix: true })}
                    </span>
                  )}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
            </Link>
          </li>
        ))}
      </ul>

      {nextCursor && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isLoading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

export default TicketListPage;
